import React, { useContext } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Mail, MapPin, ArrowUp } from 'lucide-react';
import { ContentContext } from '../App';

const Footer = () => {
  const { t, language } = useLanguage();
  const content = useContext(ContentContext);
  const company = content?.company || {};

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { label: t.nav.home, href: '#home' },
    { label: t.nav.about, href: '#about' },
    { label: t.nav.services, href: '#services' },
    { label: t.nav.projects, href: '#projects' },
    { label: t.nav.contact, href: '#contact' }
  ];

  const services = language === 'en'
    ? ['Residential Roofing', 'Commercial Roofing', 'Siding Services', 'Roof Repair', 'Emergency Service']
    : ['住宅屋顶', '商业屋顶', '壁板服务', '屋顶维修', '紧急服务'];

  return (
    <footer className="bg-slate-900 pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <a href="#home" className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
                <span className="text-slate-900 font-black text-lg">R</span>
              </div>
              <span className="text-xl font-bold text-white tracking-tight">
                ROOF<span className="text-amber-400">TOPPER</span>
              </span>
            </a>
            <p className="text-slate-400 leading-relaxed mb-6">
              {t.footer.description}
            </p>
            <div className="flex gap-4">
              <a
                href={`tel:${company.phone}`}
                className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-amber-500 transition-colors group"
              >
                <Phone className="w-5 h-5 text-slate-400 group-hover:text-slate-900" />
              </a>
              <a
                href={`mailto:${company.email}`}
                className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-amber-500 transition-colors group"
              >
                <Mail className="w-5 h-5 text-slate-400 group-hover:text-slate-900" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-6">{t.footer.quickLinks}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-slate-400 hover:text-amber-400 transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold mb-6">{t.footer.services}</h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <a
                    href="#services"
                    className="text-slate-400 hover:text-amber-400 transition-colors"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-6">{t.footer.contact}</h3>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <MapPin className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400">{company.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <a href={`tel:${company.phone}`} className="text-slate-400 hover:text-amber-400 transition-colors">
                  {company.phone}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <a href={`mailto:${company.email}`} className="text-slate-400 hover:text-amber-400 transition-colors">
                  {company.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} {company.name || 'ROOFTOPPER'}. {t.footer.rights}
          </p>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors"
          >
            <ArrowUp className="w-4 h-4" />
            {language === 'en' ? 'Back to Top' : '返回顶部'}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
